import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BasicLayout } from '@monorepo/common';
import { useLocation, useParams } from 'react-router-dom';

import {
  ResumeContactResponseAction,
  useGetResumeContactRequestPreviewQuery,
  useRespondResumeContactRequestMutation,
} from '../../services/serviceResume';

const REASON_MAX = 1000;

const resultCopy = (
  action: ResumeContactResponseAction | null,
  alreadyHandled: boolean
): string | null => {
  if (alreadyHandled) return null;
  switch (action) {
    case 'approve':
      return 'This employer can now see the contact details and resume file saved in your CPG Jobs '
        + 'profile. You can withdraw their access at any time from My Resume.';
    case 'decline':
      return 'Nothing was shared. This employer can ask again later unless you block them.';
    case 'block':
      return 'This employer can no longer see your profile or contact you through CPG Jobs, and any '
        + 'access you had already approved has been withdrawn. You can unblock them from My Resume.';
    case 'report':
      return 'Thank you. The BevNET team has the details. This employer is also blocked, any access '
        + 'you had approved has been withdrawn, and they can no longer see your profile.';
    default:
      return null;
  }
};

export const ResumeContactResponse = () => {
  const { requestGuid } = useParams<{ requestGuid: string }>();
  const location = useLocation();
  const preview = useGetResumeContactRequestPreviewQuery(requestGuid, { skip: !requestGuid });
  const [respond, response] = useRespondResumeContactRequestMutation();
  const [reasonStep, setReasonStep] = useState(false);
  const [reason, setReason] = useState('');
  const [chosen, setChosen] = useState<ResumeContactResponseAction | null>(null);
  const requestedResponse = new URLSearchParams(location.search).get('response');

  const submit = async (action: ResumeContactResponseAction) => {
    setChosen(action);
    try {
      await respond({
        requestGuid,
        action,
        reason: action === 'report' ? reason.trim() : undefined,
      }).unwrap();
    } catch {
      // The error alert below invites a retry; nothing is shared on failure.
    }
  };

  const isPending = preview.data?.status === 0;
  const followUp = response.data
    ? resultCopy(chosen, response.data.alreadyHandled)
    : null;

  return (
    <BasicLayout isLoading={preview.isLoading} helmet={<title>Resume contact request | CPG Jobs</title>}>
      <Container maxWidth="sm" sx={{ py: { xs: 5, md: 9 } }}>
        <Paper variant="outlined" sx={{ borderRadius: 0, boxShadow: '6px 6px 0 var(--accent-soft)', p: { xs: 3, md: 5 } }}>
          <Typography sx={{ color: 'var(--accent-text)', fontSize: '0.75rem', fontWeight: 700 }}>
            CPG Jobs resume privacy
          </Typography>
          <Typography component="h1" variant="h4" sx={{ my: 2 }}>An employer would like to connect</Typography>

          {/* Nothing about the employer is mounted unless the request is still
              live-Pending (see the isPending guard below). A used, declined,
              blocked, or expired token renders only the neutral notice, so a
              forwarded or leaked email link cannot keep disclosing which employer
              approached this candidate or what they wrote — not on screen and not
              in view-source.

              A deliberate residual remains: an invalid token says "no longer
              available" while a spent one says "already handled", so a holder can
              still tell that a token was once real. That is accepted rather than
              overlooked. The tokens are random GUIDs, so the distinction is worth
              nothing to an attacker who cannot guess one, and collapsing it would
              cost the candidate the confirmation that their own answer registered. */}
          {preview.error ? (
            <Alert severity="info">
              This link is no longer available. It may have already been used or expired.
            </Alert>
          ) : null}
          {response.error ? <Alert severity="error">We could not save your response. Please try again.</Alert> : null}
          {response.data ? (
            <Stack spacing={2}>
              <Alert severity={response.data.alreadyHandled ? 'info' : 'success'}>
                {response.data.message}
              </Alert>
              {followUp ? <Typography color="text.secondary">{followUp}</Typography> : null}
              {response.data.reported && response.data.reportAlertState === 'needsRetry' ? (
                <Alert severity="info">
                  Your report is saved and this employer is blocked. Our alert email is still
                  queuing, so the BevNET team may take a little longer to review it.
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {preview.data && !isPending && !response.data ? (
            <Alert severity="info">This request has already been handled or has expired.</Alert>
          ) : null}

          {preview.data && isPending && !response.data ? (
            <Stack spacing={3}>
              <Box>
                <Typography fontWeight={800}>{preview.data.employerName}</Typography>
                <Typography color="text.secondary">{preview.data.listingTitle || 'No specific listing attached'}</Typography>
              </Box>
              {preview.data.message ? (
                <Box sx={{ backgroundColor: 'var(--bg-2)', borderLeft: '3px solid var(--accent)', p: 2 }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{preview.data.message}</Typography>
                </Box>
              ) : null}
              <Alert severity="info">
                You stay in control. Sharing your details lets <strong>this employer</strong> see the
                email, phone, address, and resume file saved in your CPG Jobs profile. Anything they
                copy or save afterwards cannot be recalled. Declining shares nothing, and either
                choice leaves your resume visibility unchanged.
              </Alert>

              {reasonStep ? (
                <Stack spacing={2}>
                  <Typography fontWeight={800}>Tell us what is wrong with this request</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Reporting also blocks this employer. The BevNET team reviews every report and can
                    suspend an employer&rsquo;s Resume Database access.
                  </Typography>
                  <TextField
                    value={reason}
                    onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX))}
                    label="What happened?"
                    required
                    multiline
                    minRows={3}
                    inputProps={{ maxLength: REASON_MAX }}
                    helperText={`${REASON_MAX - reason.trim().length} characters remaining.`}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      color="error"
                      variant="contained"
                      disabled={!reason.trim() || response.isLoading}
                      onClick={() => submit('report')}
                    >
                      Submit report and block
                    </Button>
                    <Button color="inherit" onClick={() => setReasonStep(false)}>Back</Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Button
                    variant={requestedResponse === 'approve' ? 'contained' : 'outlined'}
                    disabled={response.isLoading}
                    onClick={() => submit('approve')}
                  >
                    Interested: share my contact details
                  </Button>
                  <Button
                    color="inherit"
                    variant={requestedResponse === 'decline' ? 'contained' : 'outlined'}
                    disabled={response.isLoading}
                    onClick={() => submit('decline')}
                  >
                    Not interested
                  </Button>
                  <Button
                    color="inherit"
                    variant={requestedResponse === 'block' ? 'contained' : 'outlined'}
                    disabled={response.isLoading}
                    onClick={() => submit('block')}
                  >
                    Block this employer
                  </Button>
                  <Button
                    color="error"
                    variant={requestedResponse === 'report' ? 'contained' : 'outlined'}
                    disabled={response.isLoading}
                    onClick={() => setReasonStep(true)}
                  >
                    Report and block
                  </Button>
                  <Typography color="text.secondary" variant="body2">
                    Blocking stops this employer contacting you again and withdraws any access you
                    already approved. Report it as well if the message is spam, misleading, or has
                    nothing to do with hiring.
                  </Typography>
                </Stack>
              )}
            </Stack>
          ) : null}
        </Paper>
      </Container>
    </BasicLayout>
  );
};

export default ResumeContactResponse;
